import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function useLocations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["locations", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("user_id", user!.id)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from("locations")
        .insert({ name, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from("locations")
        .update({ name })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("locations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useBulkMoveAssets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetIds,
      toLocationId,
    }: {
      assetIds?: string[];
      fromLocationId?: string;
      toLocationId: string;
    }) => {
      // If specific asset IDs provided, move those
      if (assetIds && assetIds.length > 0) {
        const { error } = await supabase
          .from("assets")
          .update({ location_id: toLocationId })
          .in("id", assetIds);
        if (error) throw error;
        return;
      }
      throw new Error("No assets specified");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
    },
  });
}

/** Move all assets from one location to another */
export function useBulkMoveByLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fromLocationId,
      toLocationId,
    }: {
      fromLocationId: string;
      toLocationId: string;
    }) => {
      const { error } = await supabase
        .from("assets")
        .update({ location_id: toLocationId })
        .eq("location_id", fromLocationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.root });
    },
  });
}
